import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

import { toast } from '../components/ui/use-toast';
import { getApiBaseUrl } from '@/config';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(...inputs));
}

/** GST field: max length and allowed pattern (alphanumeric only). */
export const GST_MAX_LENGTH = 15;
export const GST_ALPHANUMERIC_REGEX = /^[A-Za-z0-9]*$/;
export const GST_VALIDATION_MESSAGE = 'GST number must be alphanumeric and should not exceed 15 characters.';

/**
 * Sanitizes GST input to alphanumeric only and max 15 characters.
 * Returns { value, hadInvalidInput } so callers can show error when input was truncated or stripped.
 */
export function sanitizeGstInput(raw: string): { value: string; hadInvalidInput: boolean } {
  const value = raw.replace(/[^A-Za-z0-9]/g, '').slice(0, GST_MAX_LENGTH);
  const hadInvalidInput = raw.length > GST_MAX_LENGTH || !GST_ALPHANUMERIC_REGEX.test(raw);
  return { value, hadInvalidInput };
}

/** Returns true if GST value is valid (alphanumeric, max 15). */
export function isValidGst(value: string): boolean {
  return value.length <= GST_MAX_LENGTH && GST_ALPHANUMERIC_REGEX.test(value);
}

/** Returns today's date as YYYY-MM-DD in local timezone (not UTC). Use for date inputs/filters. */
export function getLocalDateString(date?: Date): string {
  const d = date || new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Computes Pay Before (due) date from "Invoice No of Day for Due" setting value.
 * Value format: "number:unit" e.g. "10:D" (10 days), "2:W" (2 weeks), "1:M" (1 month).
 * Unit: D = Days, W = Weeks, M = Months. Defaults to 10 days if invalid/missing.
 * Returns YYYY-MM-DD.
 */
export function getPayBeforeDateFromInvoiceDaysSetting(value: string): string {
  const parts = (value || '').trim().split(':');
  const count = parseInt(parts[0], 10);
  const unit = (parts[1] ?? 'D').toUpperCase();
  const d = new Date();
  if (Number.isNaN(count) || count < 0) {
    d.setDate(d.getDate() + 10);
    return getLocalDateString(d);
  }
  if (unit === 'D') d.setDate(d.getDate() + count);
  else if (unit === 'W') d.setDate(d.getDate() + count * 7);
  else if (unit === 'M') d.setMonth(d.getMonth() + count);
  else d.setDate(d.getDate() + count);
  return getLocalDateString(d);
}

// Utility function to format date in UTC for database storage
export function formatDateInIST(date: Date, selectedDate?: string): string {
  let targetDate: Date;

  if (selectedDate) {
    // If user selected a date, combine it with current time
    const selected = new Date(selectedDate);
    targetDate = new Date(
      selected.getFullYear(),
      selected.getMonth(),
      selected.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
      date.getMilliseconds()
    );
  } else {
    targetDate = date;
  }

  // Convert to IST (UTC+05:30) for database storage
  // IST offset is +5 hours and 30 minutes = +330 minutes
  const istOffset = 330; // minutes
  const istDate = new Date(targetDate.getTime() + (istOffset * 60000));

  // Format the date components in IST
  const year = istDate.getUTCFullYear();
  const month = String(istDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(istDate.getUTCDate()).padStart(2, '0');
  const hours = String(istDate.getUTCHours()).padStart(2, '0');
  const minutes = String(istDate.getUTCMinutes()).padStart(2, '0');
  const seconds = String(istDate.getUTCSeconds()).padStart(2, '0');
  const milliseconds = String(istDate.getUTCMilliseconds()).padStart(3, '0');

  // Return IST time for database storage
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
}

// Function to call CustPayment API after invoice creation
async function callCustPaymentAPI(invoiceId: number, payments: any, customer: any, companies: any[]) {
  try {
    const baseUrl = getApiBaseUrl();
    const token = localStorage.getItem('token') || '';
    const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;

    console.log('=== CUSTPAYMENT API CALL START ===');
    console.log('InvoiceID:', invoiceId);

    // Get current date in YYYY-MM-DD format (local timezone)
    const today = getLocalDateString();

    // Prepare payment methods array
    const paymentMethods = [];

    // Cash payment
    if (parseFloat(payments.cashPayment) > 0) {
      paymentMethods.push({
        InvoiceID: invoiceId,
        Amount: Math.round(parseFloat(payments.cashPayment)),
        PMode: "cash",
        PaidOn: today,
        Reference: "",
        Notes: "",
        Bank: "",
        Account: ""
      });
    }

    // Card payment
    if (parseFloat(payments.cardPayment) > 0) {
      paymentMethods.push({
        InvoiceID: invoiceId,
        Amount: Math.round(parseFloat(payments.cardPayment)),
        PMode: "card",
        PaidOn: today,
        Reference: "",
        Notes: "",
        Bank: "",
        Account: ""
      });
    }

    // Wallet payment
    if (parseFloat(payments.walletAmount) > 0) {
      console.log('=== WALLET PAYMENT DEBUG ===');
      console.log('Wallet Amount:', payments.walletAmount);
      console.log('Wallet Provider:', payments.walletProvider);
      console.log('Wallet TxNo:', payments.walletTxNo);
      console.log('============================');

      paymentMethods.push({
        InvoiceID: invoiceId,
        Amount: Math.round(parseFloat(payments.walletAmount)),
        PMode: "wallet",
        PaidOn: today,
        Reference: payments.walletTxNo || "",
        Notes: payments.walletProvider || "",
        Bank: "",
        Account: ""
      });
    }

    // Cheque payment
    if (parseFloat(payments.chequeAmount) > 0) {
      paymentMethods.push({
        InvoiceID: invoiceId,
        Amount: Math.round(parseFloat(payments.chequeAmount)),
        PMode: "cheque",
        PaidOn: today,
        Reference: payments.chequeNo || "", // Cheque No
        Notes: "",
        Bank: "",
        Account: ""
      });
    }

    // Coupon payment
    if (parseFloat(payments.couponAmount) > 0) {
      paymentMethods.push({
        InvoiceID: invoiceId,
        Amount: Math.round(parseFloat(payments.couponAmount)),
        PMode: "coupon",
        PaidOn: today,
        Reference: payments.couponNo || "", // Coupon Code
        Notes: "",
        Bank: "",
        Account: ""
      });
    }

    // Credit Note payment
    if (parseFloat(payments.creditNoteAmount) > 0) {
      paymentMethods.push({
        InvoiceID: invoiceId,
        Amount: Math.round(parseFloat(payments.creditNoteAmount)),
        PMode: "creditnote",
        PaidOn: today,
        Reference: payments.creditNoteNo || "", // Credit Note Number
        Notes: "",
        Bank: "",
        Account: ""
      });
    }

    // Points payment
    if (parseFloat(payments.pointsRedeem) > 0) {
      paymentMethods.push({
        InvoiceID: invoiceId,
        Amount: Math.round(parseFloat(payments.pointsRedeem)),
        PMode: "points",
        PaidOn: today,
        Reference: "",
        Notes: "",
        Bank: "",
        Account: ""
      });
    }

    // Call CustPayment API for each payment method
    const failedPayments = [];
    for (const paymentMethod of paymentMethods) {
      try {
        console.log('=== CUSTPAYMENT API CALL ===');
        console.log('Payment Method:', paymentMethod.PMode);
        console.log('Amount:', paymentMethod.Amount);
        console.log('InvoiceID:', paymentMethod.InvoiceID);

        // Explicit log for payment payload
        console.log('%c Payment UI Payload to Backend: ', 'background: #222; color: #bada55; font-size: 14px; font-weight: bold;', JSON.stringify([paymentMethod], null, 2));

        console.log('Request Body:', JSON.stringify([paymentMethod], null, 2));

        const response = await fetch(`${baseUrl}/api/CustPayment`, {
          method: 'POST',
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify([paymentMethod]), // Send as array as per API specification
        });

        const result = await response.json();
        console.log('CustPayment API Response:', JSON.stringify(result, null, 2));

        if (response.ok && result.status === 0) {
          console.log(`✅ CustPayment API call successful for ${paymentMethod.PMode}`);
        } else {
          console.error(`❌ CustPayment API call failed for ${paymentMethod.PMode}:`, result.message);
          failedPayments.push({
            method: paymentMethod.PMode,
            amount: paymentMethod.Amount,
            error: result.message || 'Unknown error'
          });
        }
      } catch (error) {
        console.error(`❌ CustPayment API call error for ${paymentMethod.PMode}:`, error);
        failedPayments.push({
          method: paymentMethod.PMode,
          amount: paymentMethod.Amount,
          error: error.message || 'Network error'
        });
      }
    }

    console.log(`=== CUSTPAYMENT COMPLETED ===`);
    console.log(`Total payment methods processed: ${paymentMethods.length}`);
    console.log(`Failed payments: ${failedPayments.length}`);

    // If there were any failed payments, throw an error with details
    if (failedPayments.length > 0) {
      const errorMessage = `Payment recording failed for: ${failedPayments.map(fp => `${fp.method} (₹${fp.amount})`).join(', ')}`;
      throw new Error(errorMessage);
    }

  } catch (error) {
    console.error('Error in CustPayment API calls:', error);
    throw error; // Re-throw the error to be handled by the calling function
  }
}

export async function createInvoiceApiCall({
  customer,
  products,
  payments,
  notes,
  repName,
  shipToForm,
  printInvoice,
  companyState,
  onSuccess,
  onError,
  isUnknown = false,
  invoiceDate,
  payBefore,
  partners = [],
  companies = [], // Add companies parameter
  invoiceForm = { QuoteNo: '', Mode: '', VehicleNo: '', Delivered: true, AddInFIFO: true },
  editingInvoiceId = null,
  shipToSame = false,
}) {
  try {
    // Validation (skip if isUnknown)
    if (!isUnknown) {
      if (!customer.Phone) {
        const error = new Error('Phone number is required.');
        toast({ title: 'Validation Error', description: 'Phone number is required.', variant: 'destructive' });
        if (onError) onError(error);
        return;
      }
    }
    if (!customer.SelectCompany) {
      const error = new Error('Please select a company');
      toast({ title: 'Validation Error', description: 'Please select a company', variant: 'destructive' });
      if (onError) onError(error);
      return;
    }
    if (!customer.Location) {
      const error = new Error('Please select a location');
      toast({ title: 'Validation Error', description: 'Please select a location', variant: 'destructive' });
      if (onError) onError(error);
      return;
    }
    if (!products || products.length === 0) {
      const error = new Error('At least one product is required');
      toast({ title: 'Validation Error', description: 'At least one product is required', variant: 'destructive' });
      if (onError) onError(error);
      return;
    }

    // Calculate total discount from all products
    const totalDiscount = products.reduce((sum, item) => sum + (parseFloat(item.Discount) || 0), 0);

    // Check if user has entered custom shipping data (different from customer data)
    const hasCustomShippingData = shipToForm && (
      (shipToForm.Name && shipToForm.Name.trim() !== (customer.Name || '').trim()) ||
      (shipToForm.Company && shipToForm.Company.trim() !== (customer.Company || '').trim()) ||
      (shipToForm.Phone && shipToForm.Phone.trim() !== (customer.Phone || '').trim()) ||
      (shipToForm.Email && shipToForm.Email.trim() !== (customer.Email || '').trim()) ||
      (shipToForm.GST && shipToForm.GST.trim() !== (customer.GST || '').trim()) ||
      (shipToForm.Address && shipToForm.Address.trim() !== (customer.Address || '').trim()) ||
      (shipToForm.State && shipToForm.State.trim() !== (customer.State || '').trim())
    );

    // Calculate total paid first - sum of all payment fields (₹)
    const totalPaid =
      (parseFloat(payments.cashPayment) || 0) +
      (parseFloat(payments.cardPayment) || 0) + // Use cardPayment for Card payments
      (parseFloat(payments.walletAmount) || 0) +
      (parseFloat(payments.chequeAmount) || 0) +
      (parseFloat(payments.couponAmount) || 0) +
      (parseFloat(payments.creditNoteAmount) || 0) +
      (parseFloat(payments.pointsRedeem) || 0);

    // Calculate invoice totals - we'll calculate this after processing line items
    let invoiceAmount = 0;
    // Inter-state = different states (case-insensitive, trimmed). Intra-state = same state.
    const custState = (customer.State || '').trim().toLowerCase();
    const cmpState = (companyState || '').trim().toLowerCase();
    const isIGST = custState && cmpState && custState !== cmpState;
    // Prepare invoice line items using table-calculated values (discount, GST, Cess)
    const invLineItems = products.map((item, idx) => {
      const qty = parseFloat(item.Quantity || item.Qty || 1);
      // Calculate unit price properly like mobile version (for unitprice field and fallbacks)
      let unitPrice = parseFloat(item.UnitPrice || item.unitprice || 0);
      const mrp = parseFloat(item.MRP || item.Rate || 0);
      const gstPercent = parseFloat(item.GST || item.gstper || 0);
      const cessPercent = parseFloat(item.CessPer) || 0;
      const totalTax = gstPercent + cessPercent;

      if (!unitPrice && mrp) {
        unitPrice = totalTax > 0 ? mrp / (1 + totalTax / 100) : mrp;
      }
      // Send the exact formula-derived unitPrice when we have MRP and tax (so API gets e.g. 76.92307692307692, not rounded 76.92)
      const unitPriceForApi = mrp && totalTax > 0 ? mrp / (1 + totalTax / 100) : unitPrice;

      // Use table-calculated discount (from table row)
      const discount = Math.round((parseFloat(item.Discount) || 0) * 100) / 100;

      // Full-precision taxable value for POST (same formula as UI: OfferPrice*Qty / (1 + (GST%+Cess%)/100))
      // e.g. 100*1 / (1 + 30/100) = 76.92307692307692 — send full value in tax amounts, UI keeps showing rounded 76.92
      const offerPrice = parseFloat(item.OfferPrice) || 0;
      const taxableValueFull = totalTax > 0
        ? (offerPrice * qty) / (1 + totalTax / 100)
        : offerPrice * qty;
      const gstAmountFull = taxableValueFull * gstPercent / 100;
      const cessAmountFull = taxableValueFull * cessPercent / 100;

      let sgst = 0, cgst = 0, igst = 0;
      if (isIGST) {
        igst = gstAmountFull;
      } else {
        sgst = gstAmountFull / 2;
        cgst = gstAmountFull / 2;
      }
      const cessAmount = cessAmountFull;

      // Use the OfferPrice that's already calculated in the UI (table total)
      const total = offerPrice * qty;

      // Add to invoice total
      invoiceAmount += total;

      // Debug logging for MRP/UnitPrice values
      console.log(`🔧 Product ${idx + 1} - ${item.Title}:`, {
        MRP: item.MRP,
        UnitPrice: item.UnitPrice,
        OfferPrice: item.OfferPrice,
        quantity: qty,
        discount: discount,
        total: total
      });

      // Determine if product has serial number
      const hasSerial = item.hasSerial === 1 || item.HasSerialFlag === 1 || item.hasSerial === true;
      const serialNumber = hasSerial ? (item.SerialNumber || (item.SerialDetails && item.SerialDetails[0] && item.SerialDetails[0].SerialNumber) || '') : '';

      // GST/Cess percentages for API: always send sgstper, cgstper, igstper with full values (9, 9, 18) regardless of inter/intra state
      const sgstper = String(gstPercent / 2);
      const cgstper = String(gstPercent / 2);
      const igstper = String(gstPercent);
      const cessper = String(cessPercent);

      return {
        SNo: idx + 1,
        ProdCode: Number(item.ProdCode) || Number(item.code) || 0,
        ProductName: item.Title || item.ProductName || item.Product || '',
        Available: parseFloat(item.Available) || 0,
        Barcode: item.Barcode || '',
        HSN: item.HSN || '',
        unitprice: unitPriceForApi,
        quantity: qty,
        discount,
        sgst,
        cgst,
        igst,
        cess: cessAmount,
        total: Math.round(total * 100) / 100, // Round total to 2 decimal places
        costprice: parseFloat(item.OriginalCost || item.costprice || item.CostPrice) || 0,
        imgstr: item.imgstr || item.imgStr || '',
        mrp: parseFloat(item.MRP) || 0,
        sgstper,
        cgstper,
        igstper,
        cessper,
        deliveredQty: invoiceForm.Delivered ? (parseFloat(item.DelQty || item.DeliveredQty || item.deliveredQty) || 0) : 0,
        hasSerial: hasSerial,
        serialNumber: serialNumber,
      };
    });

    // Round invoiceAmount to 2 decimal places for intermediate calculation
    const invoiceAmountRaw = Math.round(invoiceAmount * 100) / 100;
    // Use rounded invoice total for API (match UI display)
    const invoiceAmountRounded = Math.round(invoiceAmountRaw);
    invoiceAmount = invoiceAmountRaw;

    // Calculate due amount - use rounded values for API consistency
    // DueAmt = max(0, rounded(InvoiceAmount - TotalPaid))
    const totalPaidRounded = Math.round(totalPaid);
    const dueAmt = Math.max(0, invoiceAmountRounded - totalPaidRounded);

    // Additional verification: ensure DueAmt is never negative
    if (dueAmt < 0) {
      console.error('ERROR: DueAmt calculated as negative:', dueAmt);
      console.error('InvoiceAmount:', invoiceAmount);
      console.error('TotalPaid:', totalPaid);
      console.error('Payment fields:', payments);
    }

    // Debug logging for amount calculations
    console.log('=== AMOUNT CALCULATIONS DEBUG ===');
    console.log('InvoiceAmount (sum of line items):', invoiceAmount);
    console.log('TotalPaid (sum of payment fields):', totalPaid);
    console.log('DueAmt (max(0, InvoiceAmount - TotalPaid)):', dueAmt);
    console.log('Payment breakdown:', {
      CashAmt: parseFloat(payments.cashPayment) || 0,
      CardAmt: parseFloat(payments.cardPayment) || 0, // Map cardPayment to CardAmt for POST request
      WalletAmt: parseFloat(payments.walletAmount) || 0,
      ChequeAmt: parseFloat(payments.chequeAmount) || 0,
      CouponAmt: parseFloat(payments.couponAmount) || 0,
      CreditNoteAmt: parseFloat(payments.creditNoteAmount) || 0,
      PaidbyPoints: parseFloat(payments.pointsRedeem) || 0,
      PaidbyOthers: parseFloat(payments.otherAmount) || 0,
    });

    // Verify calculation matches UI
    const sumOfPayments = (parseFloat(payments.cashPayment) || 0) +
      (parseFloat(payments.cardPayment) || 0) +
      (parseFloat(payments.walletAmount) || 0) +
      (parseFloat(payments.chequeAmount) || 0) +
      (parseFloat(payments.couponAmount) || 0) +
      (parseFloat(payments.creditNoteAmount) || 0) +
      (parseFloat(payments.pointsRedeem) || 0);

    console.log('=== CALCULATION VERIFICATION ===');
    console.log('Sum of all payment amounts:', sumOfPayments);
    console.log('InvoiceAmount from line items:', invoiceAmount);
    console.log('Difference (InvoiceAmount - SumOfPayments):', invoiceAmount - sumOfPayments);
    console.log('DueAmt calculation:', dueAmt);
    console.log('Payment field values being sent to API:', {
      CashAmt: Math.round((parseFloat(payments.cashPayment) || 0) * 100) / 100,
      CardAmt: Math.round((parseFloat(payments.cardPayment) || 0) * 100) / 100,
      WalletAmt: Math.round((parseFloat(payments.walletAmount) || 0) * 100) / 100,
      ChequeAmt: Math.round((parseFloat(payments.chequeAmount) || 0) * 100) / 100,
      CouponAmt: Math.round((parseFloat(payments.couponAmount) || 0) * 100) / 100,
      CreditNoteAmt: Math.round((parseFloat(payments.creditNoteAmount) || 0) * 100) / 100,
      BankTransAmt: Math.round((parseFloat(payments.bankTransAmount) || 0) * 100) / 100,
      PaidbyPoints: Math.round((parseFloat(payments.pointsRedeem) || 0) * 100) / 100,
      PaidbyOthers: 0,
      DueAmt: dueAmt
    });
    console.log('==================================');

    // Use the date from the invoice date field; if empty, default to today (YYYY-MM-DD)
    const effectiveInvoiceDate = (invoiceDate != null && String(invoiceDate).trim() !== '')
      ? String(invoiceDate).trim()
      : new Date().toISOString().slice(0, 10);

    // Prepare invoice data according to API specification
    const invoiceData = {
      ...(editingInvoiceId && { InvoiceID: editingInvoiceId }), // Include InvoiceID for updates
      FName: customer.Name || '',
      LName: customer.Company || '',
      Mobile1: customer.Mobile1 || customer.Phone || '',
      Email: customer.Email || '',
      GSTN: customer.GSTN || customer.GST || '',
      Address: customer.Address1 || customer.Address || '',
      State: customer.State || '',
      Locality: customer.Locality || '',
      ReferredBy: customer.ReferredBy || '',
      InvDate: formatDateInIST(new Date(), effectiveInvoiceDate),
      RepName: repName || '',
      CompanyID: customer.SelectCompany || '',
      LocationID: customer.Location || '',
      eCommPartner: customer.eCommOrderID || '',
      eCommOrderID: (() => {
        if (customer.Partner && partners.length > 0) {
          const selectedPartner = partners.find(p => String(p.Id) === String(customer.Partner));
          return selectedPartner ? selectedPartner.PartnerName : '';
        }
        return '';
      })(),
      CashAmt: Math.round(parseFloat(payments.cashPayment) || 0),
      CardAmt: Math.round(parseFloat(payments.cardPayment) || 0), // Map cardPayment to CardAmt for POST request
      WalletAmt: Math.round(parseFloat(payments.walletAmount) || 0),
      ChequeAmt: Math.round(parseFloat(payments.chequeAmount) || 0),
      CouponCode: (payments.couponNo || '').trim(),
      CouponAmt: Math.round(parseFloat(payments.couponAmount) || 0),
      CreditNoteAmt: Math.round(parseFloat(payments.creditNoteAmount) || 0),
      BankTransAmt: Math.round(parseFloat(payments.bankTransAmount) || 0),
      PaidbyPoints: Math.round(parseFloat(payments.pointsRedeem) || 0),
      PaidbyOthers: 0, // Set to 0 for card payments - card amount goes to CardAmt only
      DueAmt: dueAmt, // Rounded: max(0, round(InvoiceAmount) - round(TotalPaid))
      CashPaid: Math.round((parseFloat(payments.cashPaid) || 0) * 100) / 100,
      InvoiceAmount: invoiceAmountRounded,
      InvoicedAmount: invoiceAmountRounded,
      BankID: payments.bankId || '',
      TransRefNo: payments.walletTxNo || '', // Map walletTxNo to TransRefNo for wallet transactions
      CreatedBy: localStorage.getItem('username') || 'admin_user',
      // Shipping data logic:
      // If shipToSame is true: send customer data to shipping fields
      // If shipToSame is false: send custom shipping data from popup
      ShippingName: shipToSame ? (customer.Name || '') : (shipToForm?.Name?.trim() || ''),
      ShippingCompany: shipToSame ? (customer.Company || '') : (shipToForm?.Company?.trim() || ''),
      ShippingPhone: shipToSame ? (customer.Phone || '') : (shipToForm?.Phone?.trim() || ''),
      ShippingEmail: shipToSame ? (customer.Email || '') : (shipToForm?.Email?.trim() || ''),
      ShippingGST: shipToSame ? (customer.GST || '') : (shipToForm?.GST?.trim() || ''),
      ShippingAddress: shipToSame ? (customer.Address || '') : (shipToForm?.Address?.trim() || ''),
      ShippingState: shipToSame ? (customer.State || '') : (shipToForm?.State?.trim() || ''),
      // More fields (add as needed)
      Discount: totalDiscount || 0, // Send as number, not string
      invoiceNotes: notes || '', // Changed from Notes to invoiceNotes
      // Add Tag1, Tag2, Tag3 fields from mobile form
      Tag1: invoiceForm?.QuoteNo || '',
      Tag2: invoiceForm?.Mode || '',
      Tag3: invoiceForm?.VehicleNo || '',
      invLineItems,
    };

    // Add DuePaymentBefore field conditionally
    console.log('=== DUE PAYMENT DEBUG ===');
    console.log('dueAmt:', dueAmt);
    console.log('payBefore:', payBefore);
    console.log('dueAmt !== 0:', dueAmt !== 0);
    console.log('payBefore exists:', !!payBefore);
    console.log('Condition met:', dueAmt !== 0 && payBefore);

    // Add DuePaymentBefore when there's any outstanding amount (positive or negative) and payBefore is set
    if (dueAmt !== 0 && payBefore) {
      (invoiceData as any).DuePaymentBefore = payBefore;
      console.log('DuePaymentBefore added:', payBefore);
    } else {
      console.log('DuePaymentBefore NOT added - condition not met');
    }
    console.log('=== END DUE PAYMENT DEBUG ===');
    const baseUrl = getApiBaseUrl();
    const token = localStorage.getItem('token') || '';
    const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    // Debug logging
    console.log('=== INVOICE API REQUEST DATA ===');
    console.log('API Endpoint:', `${baseUrl}/api/Invoice`);
    console.log('Request Method: POST');
    console.log('Operation Type:', editingInvoiceId ? 'UPDATE' : 'CREATE');
    console.log('Editing Invoice ID:', editingInvoiceId || 'N/A (New Invoice)');
    console.log('InvoiceID in payload:', invoiceData.InvoiceID || 'NOT INCLUDED');
    console.log('eCommPartner (eComm# textbox value):', invoiceData.eCommPartner);
    console.log('eCommOrderID (partner dropdown value):', invoiceData.eCommOrderID);
    console.log('Total Discount:', invoiceData.Discount);
    console.log('Ship to Same:', shipToSame);
    console.log('Customer Data:', {
      Name: customer.Name,
      Company: customer.Company,
      Phone: customer.Phone,
      Email: customer.Email,
      GST: customer.GST,
      Address: customer.Address,
      State: customer.State,
    });
    console.log('Ship To Form Data:', {
      Name: shipToForm?.Name,
      Company: shipToForm?.Company,
      Phone: shipToForm?.Phone,
      Email: shipToForm?.Email,
      GST: shipToForm?.GST,
      Address: shipToForm?.Address,
      State: shipToForm?.State,
    });
    console.log('Final Shipping Data Sent:', {
      ShippingName: invoiceData.ShippingName,
      ShippingCompany: invoiceData.ShippingCompany,
      ShippingPhone: invoiceData.ShippingPhone,
      ShippingEmail: invoiceData.ShippingEmail,
      ShippingGST: invoiceData.ShippingGST,
      ShippingAddress: invoiceData.ShippingAddress,
      ShippingState: invoiceData.ShippingState,
    });
    console.log('Authorization:', authToken ? 'Bearer Token Present' : 'No Token');
    console.log('JSON Payload:', JSON.stringify(invoiceData, null, 2));
    console.log('=====================================');

    // Explicit debug logs requested for troubleshooting
    console.log('----- DEBUG: INVOICE API CALL START -----');
    console.log('Using API Endpoint:', `${baseUrl}/api/Invoice`);
    console.log('Request Method:', 'POST');
    console.log('Operation:', editingInvoiceId ? 'EDIT/UPDATE' : 'CREATE');

    // Explicitly requested log format "what body my ui is sending to the backend"
    console.log('%c UI Payload to Backend: ', 'background: #222; color: #bada55; font-size: 14px; font-weight: bold;', JSON.stringify(invoiceData, null, 2));

    console.log('Request Body (Raw):', invoiceData);
    console.log('----- DEBUG: INVOICE API CALL END -----');

    // API call
    const response = await fetch(`${baseUrl}/api/Invoice`, {
      method: 'POST',
      headers: {
        'Authorization': authToken,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(invoiceData),
    });
    const result = await response.json();
    console.log('=== API RESPONSE ===');
    console.log('Response Status:', response.status);
    console.log('Response OK:', response.ok);
    console.log('Response Data:', JSON.stringify(result, null, 2));
    if (response.ok && result.status === 0) {
      // Extract InvoiceID and InvoiceNumber from response for CustPayment API calls
      let invoiceId = null;
      let invoiceNumber = null;

      console.log('=== EXTRACTING INVOICE ID AND NUMBER ===');
      console.log('Edit Mode:', !!editingInvoiceId);
      console.log('Editing Invoice ID:', editingInvoiceId);
      console.log('API Response:', JSON.stringify(result, null, 2));

      // Extract InvoiceID (Column2)
      if (result?.data?.[0]?.Column2) {
        invoiceId = result.data[0].Column2;
        console.log('Extracted InvoiceID from result.data[0].Column2:', invoiceId);
      } else if (result?.data?.data?.[0]?.Column2) {
        invoiceId = result.data.data[0].Column2;
        console.log('Extracted InvoiceID from result.data.data[0].Column2:', invoiceId);
      } else if (result?.Column2) {
        invoiceId = result.Column2;
        console.log('Extracted InvoiceID from result.Column2:', invoiceId);
      }

      // Extract InvoiceNumber (Column1)
      if (result?.data?.[0]?.Column1) {
        invoiceNumber = result.data[0].Column1;
        console.log('Extracted InvoiceNumber from result.data[0].Column1:', invoiceNumber);
      } else if (result?.data?.data?.[0]?.Column1) {
        invoiceNumber = result.data.data[0].Column1;
        console.log('Extracted InvoiceNumber from result.data.data[0].Column1:', invoiceNumber);
      } else if (result?.Column1) {
        invoiceNumber = result.Column1;
        console.log('Extracted InvoiceNumber from result.Column1:', invoiceNumber);
      }

      // In edit mode, if invoiceId extraction failed, use the editingInvoiceId as fallback
      if (!invoiceId && editingInvoiceId) {
        invoiceId = editingInvoiceId;
        console.log('Using editingInvoiceId as fallback for InvoiceID:', invoiceId);
      }

      console.log('Final InvoiceID for CustPayment:', invoiceId);
      console.log('Final InvoiceNumber for display:', invoiceNumber);
      console.log('=====================================');

      // Call CustPayment API after successful invoice creation/update
      if (invoiceId) {
        console.log('=== CALLING CUSTPAYMENT API ===');
        console.log('Invoice ID for CustPayment:', invoiceId);
        console.log('Edit Mode:', !!editingInvoiceId);
        try {
          await callCustPaymentAPI(invoiceId, payments, customer, companies); // Pass companies here
          // If CustPayment API succeeds, show complete success message
          const operationType = editingInvoiceId ? 'updated' : 'created';
          const invoiceNumberText = invoiceNumber ? ` #${invoiceNumber}` : '';
          toast({ title: 'Success', description: `Invoice${invoiceNumberText} ${operationType} and payments recorded successfully!`, className: 'bg-green-600 text-white border-green-700' });
        } catch (custPaymentError) {
          console.error('Error calling CustPayment API:', custPaymentError);
          // Show warning that invoice was created but payment recording failed
          const operationType = editingInvoiceId ? 'updated' : 'created';
          const invoiceNumberText = invoiceNumber ? ` #${invoiceNumber}` : '';
          toast({
            title: 'Partial Success',
            description: `Invoice${invoiceNumberText} ${operationType} successfully, but payment recording failed: ${custPaymentError.message}`,
            className: 'bg-yellow-600 text-white border-yellow-700'
          });
          // Still call onSuccess since invoice was created, but also call onError for payment failure
          if (onSuccess) onSuccess(result);
          if (onError) onError(custPaymentError);
          return; // Exit early to prevent duplicate success callback
        }
      } else {
        console.warn('No Invoice ID found for CustPayment API calls');
        // Show success message for invoice creation only
        const operationType = editingInvoiceId ? 'updated' : 'created';
        const invoiceNumberText = invoiceNumber ? ` #${invoiceNumber}` : '';
        toast({ title: 'Success', description: `Invoice${invoiceNumberText} ${operationType} successfully!`, className: 'bg-green-600 text-white border-green-700' });
      }

      if (onSuccess) onSuccess(result);
    } else {
      const operationType = editingInvoiceId ? 'update' : 'create';
      throw new Error(result.message || `Failed to ${operationType} invoice`);
    }
  } catch (error) {
    console.error('Error creating invoice:', error);
    toast({ title: 'Error', description: error.message || 'Failed to create invoice. Please try again.', variant: 'destructive' });
    if (onError) onError(error);
  }
}
